import { type ComponentProps } from 'react';

// Type-only import so the `{@link Grid}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Grid } from './Grid';
import { cn } from './utils/cn';

/**
 * Cross-axis alignment of children (maps to CSS `align-items`).
 *
 * The resolved default depends on direction: `'center'` for horizontal, `'start'` for vertical.
 */
export type StackAlign = 'baseline' | 'center' | 'end' | 'start' | 'stretch';

/**
 * Flex direction. `'vertical'` lays children out top-to-bottom, `'horizontal'`
 * lays them left-to-right.
 */
export type StackDirection = 'horizontal' | 'vertical';

/**
 * Allowed values for the `gap` prop, mapped 1:1 to Tailwind's spacing scale
 * (e.g. `gap={3}` → `gap-3` → 0.75rem).
 */
export type StackGap =
  | 0
  | 0.5
  | 1
  | 1.5
  | 2
  | 2.5
  | 3
  | 3.5
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 12
  | 14
  | 16;

/**
 * Main-axis distribution of children (maps to CSS `justify-content`).
 *
 * Defaults to `'start'` (flex-start) when not set.
 */
export type StackJustify =
  | 'around'
  | 'between'
  | 'center'
  | 'end'
  | 'evenly'
  | 'start'
  | 'stretch';

const ALIGN_MAP: Record<StackAlign, string> = {
  baseline: 'items-baseline',
  center: 'items-center',
  end: 'items-end',
  start: 'items-start',
  stretch: 'items-stretch',
};

const DIRECTION_MAP: Record<StackDirection, string> = {
  horizontal: 'flex-row',
  vertical: 'flex-col',
};

const GAP_MAP: Record<StackGap, string> = {
  0: 'gap-0',
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  2.5: 'gap-2.5',
  3: 'gap-3',
  3.5: 'gap-3.5',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  7: 'gap-7',
  8: 'gap-8',
  9: 'gap-9',
  10: 'gap-10',
  12: 'gap-12',
  14: 'gap-14',
  16: 'gap-16',
};

const JUSTIFY_MAP: Record<StackJustify, string> = {
  around: 'justify-around',
  between: 'justify-between',
  center: 'justify-center',
  end: 'justify-end',
  evenly: 'justify-evenly',
  start: 'justify-start',
  stretch: 'justify-stretch',
};

/**
 * Props for the {@link Stack} component.
 *
 * Extends standard `<div>` props so `ref`, event handlers, `data-*`,
 * `aria-*`, `style`, etc. pass through to the underlying element.
 */
export type StackProps = ComponentProps<'div'> & {
  /** Cross-axis alignment. @default `'center'` when horizontal, `'start'` when vertical */
  align?: StackAlign;
  /** Flex direction. @default 'vertical' */
  direction?: StackDirection;
  /** When true, applies `h-full`. @default false */
  fullHeight?: boolean;
  /**
   * When true, applies `w-full` so the Stack fills its parent's width.
   * Set to `false` when the Stack is itself a flex item and should be
   * content-width (e.g. a toolbar Stack inside another horizontal Stack).
   * @default true
   */
  fullWidth?: boolean;
  /** Space between children on Tailwind's spacing scale (`gap={3}` → `gap-3`). @default 0 */
  gap?: StackGap;
  /**
   * Renders as `inline-flex` instead of `flex` (capped to parent width via
   * `max-w-full`). Use when the Stack needs to sit inline within text or
   * when another element relies on the Stack not being block-level.
   * @default false
   */
  inline?: boolean;
  /** Main-axis distribution. @default `'start'` (no class applied). */
  justify?: StackJustify;
  /** Enables `flex-wrap` so children wrap to new lines when space runs out. @default false */
  wrap?: boolean;
};

/**
 * Flex container with typed layout props. Use for any row- or column-based
 * layout.
 *
 * Defaults to `direction="vertical"` with `align="start"`. For horizontal
 * rows, `align` defaults to `'center'` which matches the common "icon +
 * label" pattern.
 *
 * See also: {@link Grid} for CSS grid layouts; raw `<div className="...">`
 * for containers that don't need flex/grid layout.
 *
 * @example
 * // Vertical stack of cards (default direction)
 * <Stack gap={3}>
 *   <Card />
 *   <Card />
 * </Stack>
 *
 * @example
 * // Horizontal toolbar with icon + label
 * <Stack direction="horizontal" gap={2} align="center">
 *   <Icon name="Check" />
 *   <Text>Saved</Text>
 * </Stack>
 *
 * @example
 * // Wrapping tag row, content-width so it sits next to siblings
 * <Stack direction="horizontal" wrap gap={2} fullWidth={false}>
 *   {tags.map((t) => <Tag key={t.id} label={t.label} />)}
 * </Stack>
 */
export function Stack({
  align,
  className,
  direction = 'vertical',
  fullHeight = false,
  fullWidth = true,
  gap = 0,
  inline,
  justify,
  ref,
  wrap,
  ...props
}: StackProps) {
  const resolvedAlign =
    align ?? (direction === 'horizontal' ? 'center' : 'start');

  return (
    <div
      ref={ref}
      className={cn(
        inline ? 'inline-flex max-w-full' : 'flex',
        DIRECTION_MAP[direction],
        ALIGN_MAP[resolvedAlign],
        justify && JUSTIFY_MAP[justify],
        GAP_MAP[gap],
        wrap && 'flex-wrap',
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        className,
      )}
      {...props}
    />
  );
}
